import type {
  CaptureInfo,
  CoordinateCalibration,
  GameState,
  MapAsset,
  OutpostState,
  PlayerState,
  PlayerStatistics,
  QuestLink,
  QuestObjective,
  QuestState,
  ResourceRequirement
} from '../../types/game'
import { buildRoutePlan } from '../planner/routePlanner'

const MAX_FILE_SIZE = 64 * 1024 * 1024
const V4_SCHEMA_VERSION = '2.0.0'
const V4_DOCUMENT_TYPE = 'CrashlandsAssistantInput'

export class SaveParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SaveParseError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function numberValue(value: unknown, fallback = 0): number {
  const result = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(result) ? result : fallback
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const result = numberValue(value, Number.NaN)
  return Number.isFinite(result) ? result : null
}

function textValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function isDamagedText(value: string): boolean {
  if (!value) return true
  const replacementCount = [...value].filter((character) => character === '�').length
  return replacementCount >= Math.max(2, Math.floor(value.length * 0.2))
}

function localizedText(chinese: unknown, english: unknown, fallback = ''): string {
  const chineseText = textValue(chinese)
  return chineseText && !isDamagedText(chineseText)
    ? chineseText
    : textValue(english, fallback)
}

function booleanValue(value: unknown): boolean {
  return value === true || value === 1
}

function parseCapture(
  root: Record<string, unknown>,
  integrityStatus: CaptureInfo['integrityStatus']
): CaptureInfo {
  const capture = asRecord(root.capture)
  const tool = asRecord(root.tool)

  return {
    capturedAt: textValue(capture.captured_at),
    saveId: textValue(capture.save_id, '未知存档'),
    sourceKind: textValue(capture.source_kind, 'unknown'),
    toolVersion: textValue(tool.version, '4.x'),
    schemaVersion: textValue(root.schema_version),
    integrityStatus
  }
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`

  const record = value as Record<string, unknown>
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
    .join(',')}}`
}

async function sha256Hex(value: string): Promise<string> {
  return sha256Bytes(new TextEncoder().encode(value))
}

async function sha256Bytes(value: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(value).buffer)
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function decodeBase64(base64: string): Uint8Array {
  const binary = atob(base64)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}

async function verifyEvidenceGroup(value: unknown): Promise<number> {
  let verified = 0
  for (const entry of Object.values(asRecord(value))) {
    const record = asRecord(entry)
    const expected = textValue(record.sha256).toLowerCase()
    const encoded = textValue(record.data)
    if (!encoded || !/^[a-f0-9]{64}$/.test(expected)) continue
    const actual = await sha256Bytes(decodeBase64(encoded))
    if (actual !== expected) {
      throw new SaveParseError('v4 文件中的原始存档证据哈希校验失败。')
    }
    verified += 1
  }
  return verified
}

async function verifyIntegrity(root: Record<string, unknown>): Promise<CaptureInfo['integrityStatus']> {
  const rawEvidence = asRecord(root.raw_evidence)
  const evidenceCount = await verifyEvidenceGroup(rawEvidence.save_files)
    + await verifyEvidenceGroup(rawEvidence.rumpus_files)
    + await verifyEvidenceGroup(rawEvidence.map_files)
  const integrity = asRecord(root.integrity)
  const expected = textValue(integrity.sha256).toLowerCase()
  if (!expected) return evidenceCount > 0 ? 'evidence-verified' : 'missing'
  if (
    textValue(integrity.algorithm) !== 'sha256-canonical-json'
    || textValue(integrity.scope) !== 'all top-level fields except integrity'
  ) {
    throw new SaveParseError('v4 文件使用了不支持的完整性算法或校验范围。')
  }

  const unsigned = Object.fromEntries(
    Object.entries(root).filter(([key]) => key !== 'integrity')
  )
  const actual = await sha256Hex(canonicalJson(unsigned))
  return actual === expected ? 'verified' : evidenceCount > 0 ? 'evidence-verified' : 'not-checked'
}

function parsePlayer(save: Record<string, unknown>): PlayerState {
  const header = asRecord(save.header)
  return {
    x: numberValue(header.player_world_x),
    y: numberValue(header.player_world_y),
    biome: numberValue(header.current_biome),
    playtimeHours: numberValue(header.playtime_hours)
  }
}

function parseStatistics(save: Record<string, unknown>): PlayerStatistics {
  const statistics = asRecord(save.player_statistics)
  return {
    questsCompleted: numberValue(statistics['Quests Completed']),
    creaturesDefeated: numberValue(statistics['Creatures Defeated']),
    itemsCrafted: numberValue(statistics['Items Crafted']),
    fishCaught: numberValue(statistics['Fish Caught']),
    deaths: numberValue(statistics.Deaths)
  }
}

function parseQuestLinks(value: unknown): QuestLink[] {
  return asArray(value).flatMap((entry) => {
    const item = asRecord(entry)
    const questId = nullableNumber(item.quest_id ?? item.quest)
    if (questId === null) return []
    return [{
      questId,
      name: localizedText(item.name_zh, item.name_en, `任务 ${questId}`)
    }]
  })
}

function parseObjectives(value: unknown): QuestObjective[] {
  return asArray(value).flatMap((entry, fallbackIndex) => {
    const task = asRecord(entry)
    if (Object.keys(task).length === 0) return []

    const current = Math.max(0, numberValue(task.current))
    const required = Math.max(0, numberValue(task.required))
    const itemId = nullableNumber(task.item_id ?? task.loot_item_id ?? task.resource_item_id)
    const outpostId = nullableNumber(task.outpost_id)
    const targetName = textValue(
      task.target_name_en
        ?? task.item_name_en
        ?? task.loot_item_name_en
        ?? task.resource_item_name_en
        ?? task.creature_name_en,
      localizedText(task.outpost_name_zh, task.outpost_name_en, '未命名目标')
    )

    return [{
      index: numberValue(task.index, fallbackIndex),
      type: numberValue(task.type),
      current,
      required,
      missing: Math.max(0, required - current),
      completed: booleanValue(task.objective_met_inferred) || current >= required,
      targetName,
      itemId,
      outpostId,
      outpostName: outpostId === null
        ? null
        : localizedText(task.outpost_name_zh, task.outpost_name_en, `据点 ${outpostId}`)
    }]
  })
}

function parseQuests(save: Record<string, unknown>, outposts: OutpostState[]): QuestState[] {
  const outpostById = new Map(outposts.map((outpost) => [outpost.id, outpost]))

  return asArray(save.active_quests).flatMap((entry) => {
    const quest = asRecord(entry)
    const id = nullableNumber(quest.quest_id)
    if (id === null) return []

    const objectives = parseObjectives(quest.tasks)
    const targetObjective = objectives.find((objective) => objective.outpostId !== null)
    const targetOutpost = targetObjective?.outpostId === null || targetObjective?.outpostId === undefined
      ? null
      : outpostById.get(targetObjective.outpostId) ?? null

    return [{
      id,
      title: localizedText(quest.name_zh, quest.name_en, `任务 ${id}`),
      englishTitle: textValue(quest.name_en),
      storyName: localizedText(quest.story_name_zh, quest.story_name_en),
      giverName: localizedText(quest.giver_name_zh, quest.giver_name_en, '未知'),
      receiverName: localizedText(quest.receiver_name_zh, quest.receiver_name_en, '未知'),
      objectiveStateRaw: numberValue(quest.objective_state_raw),
      objectivesMet: booleanValue(quest.all_defined_objectives_met_inferred)
        || objectives.every((objective) => objective.completed),
      objectives,
      requires: parseQuestLinks(asRecord(quest.requires).quests),
      unlocks: parseQuestLinks(quest.unlocks_on_completion),
      dialogue: asArray(quest.incompletion_dialogue_zh)
        .map((line) => textValue(line))
        .filter(Boolean),
      targetBiome: targetOutpost?.biome ?? null,
      targetOutpostId: targetObjective?.outpostId ?? null,
      targetOutpostName: targetObjective?.outpostName ?? null,
      evidence: 'official_or_code_mapping'
    }]
  })
}

function parseOutposts(save: Record<string, unknown>): OutpostState[] {
  const nearest = asRecord(save.nearest_outpost_inferred)
  const nearestId = nullableNumber(nearest.outpost_id)

  return asArray(save.outpost_states).flatMap((entry) => {
    const outpost = asRecord(entry)
    const id = nullableNumber(outpost.outpost_id)
    if (id === null) return []
    const isNearest = id === nearestId

    return [{
      id,
      name: localizedText(outpost.outpost_name_zh, outpost.outpost_name_en, `据点 ${id}`),
      englishName: textValue(outpost.outpost_name_en),
      biome: numberValue(outpost.biome, -1),
      stageId: numberValue(outpost.stage_id),
      stageName: textValue(outpost.stage_name, '未知阶段'),
      visible: booleanValue(outpost.visibility_flag),
      estimatedWorldX: isNearest ? nullableNumber(nearest.estimated_center_x) : null,
      estimatedWorldY: isNearest ? nullableNumber(nearest.estimated_center_y) : null,
      evidence: isNearest ? 'unverified_inference' : 'direct_save_evidence'
    }]
  })
}

function parseResources(quests: QuestState[]): ResourceRequirement[] {
  const requirements = new Map<string, ResourceRequirement>()

  for (const quest of quests) {
    for (const objective of quest.objectives) {
      if (objective.itemId === null) continue
      const key = `item:${objective.itemId}`
      const existing = requirements.get(key)

      if (existing) {
        existing.required += objective.required
        existing.current = Math.max(existing.current, objective.current)
        existing.missing = Math.max(0, existing.required - existing.current)
        existing.ready = existing.missing === 0
        existing.questIds.push(quest.id)
        existing.questNames.push(quest.title)
      } else {
        requirements.set(key, {
          key,
          itemId: objective.itemId,
          name: objective.targetName,
          current: objective.current,
          required: objective.required,
          missing: objective.missing,
          ready: objective.missing === 0,
          questIds: [quest.id],
          questNames: [quest.title]
        })
      }
    }
  }

  return [...requirements.values()].sort((left, right) => {
    if (left.ready !== right.ready) return left.ready ? 1 : -1
    return left.missing - right.missing
  })
}

function inferBiomeFromFileName(fileName: string): number {
  const match = fileName.match(/^maptex_(\d+)_/)
  return match ? numberValue(match[1], -1) : -1
}

function base64FromMapRecord(record: Record<string, unknown>): string | null {
  for (const key of ['data', 'data_base64', 'base64', 'png_base64']) {
    const candidate = textValue(record[key])
    if (candidate) return candidate.replace(/^data:image\/png;base64,/, '')
  }
  return null
}

function base64ToObjectUrl(base64: string): string {
  try {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index)
    }
    return URL.createObjectURL(new Blob([bytes], { type: 'image/png' }))
  } catch {
    throw new SaveParseError('地图像素的 Base64 数据无效。')
  }
}

function parseMaps(rawEvidence: Record<string, unknown>): MapAsset[] {
  const mapFiles = asRecord(rawEvidence.map_files)

  return Object.entries(mapFiles)
    .filter(([fileName]) => fileName.toLowerCase().endsWith('.png'))
    .map(([fileName, value]) => {
      const map = asRecord(value)
      const base64 = base64FromMapRecord(map)
      return {
        fileName,
        biome: numberValue(map.biome, inferBiomeFromFileName(fileName)),
        width: numberValue(map.width),
        height: numberValue(map.height),
        sha256: textValue(map.sha256),
        sizeBytes: numberValue(map.size_bytes),
        pixelStatus: base64 ? 'ready' : 'metadata-only',
        objectUrl: base64 ? base64ToObjectUrl(base64) : null
      }
    })
}

function buildDefaultCalibrations(maps: MapAsset[]): CoordinateCalibration[] {
  return maps.map((map) => ({
    biome: map.biome,
    status: 'unverified',
    worldOriginX: 0,
    worldOriginY: 0,
    pixelOriginX: map.width / 2,
    pixelOriginY: map.height / 2,
    pixelsPerWorldX: 1,
    pixelsPerWorldY: 1
  }))
}

function validateV4Root(root: Record<string, unknown>) {
  const schemaVersion = textValue(root.schema_version)
  const documentType = textValue(root.document_type)

  if (schemaVersion !== V4_SCHEMA_VERSION || documentType !== V4_DOCUMENT_TYPE) {
    throw new SaveParseError(
      `不支持的数据协议。需要 v4 单文件协议 ${V4_SCHEMA_VERSION}，当前为 ${schemaVersion || '未知版本'}。`
    )
  }
}

export async function parseSave(file: File): Promise<GameState> {
  if (file.size > MAX_FILE_SIZE) {
    throw new SaveParseError('文件超过 64 MB，当前版本拒绝处理。')
  }

  let raw: unknown
  try {
    raw = JSON.parse(await file.text())
  } catch {
    throw new SaveParseError('文件不是有效的 JSON 数据。')
  }

  if (!isRecord(raw)) {
    throw new SaveParseError('存档根节点必须是一个对象。')
  }
  const integrityStatus = await verifyIntegrity(raw)
  return parseV4Document(raw, integrityStatus)
}

export function parseV4Document(
  raw: unknown,
  integrityStatus: CaptureInfo['integrityStatus'] = 'not-checked'
): GameState {
  if (!isRecord(raw)) {
    throw new SaveParseError('存档根节点必须是一个对象。')
  }
  validateV4Root(raw)

  const semantic = asRecord(raw.semantic)
  const save = asRecord(semantic.save_v381)
  if (Object.keys(save).length === 0) {
    throw new SaveParseError('文件缺少 semantic.save_v381，无法读取 v381 存档语义。')
  }

  const outposts = parseOutposts(save)
  const quests = parseQuests(save, outposts)
  const player = parsePlayer(save)
  const maps = parseMaps(asRecord(raw.raw_evidence))
  const calibrations = buildDefaultCalibrations(maps)
  const resources = parseResources(quests)
  const warnings: string[] = []

  if (maps.length === 0) {
    warnings.push('该文件没有地图记录。')
  } else if (maps.every((map) => map.pixelStatus !== 'ready')) {
    warnings.push('该 v4 文件只包含地图元数据，没有 PNG 像素；任务分析仍可使用。')
  }
  const supportedParserStatuses = new Set([
    'supported_v381_partial_semantic_decode',
    'global_v381_header_outposts_active_quests_verified'
  ])
  if (!supportedParserStatuses.has(textValue(semantic.parser_status))) {
    warnings.push(`语义解析器状态：${textValue(semantic.parser_status, '未知')}`)
  }
  if (integrityStatus === 'evidence-verified') {
    warnings.push('原始存档证据哈希已验证；Python 与浏览器的浮点规范化不同，顶层 canonical JSON 哈希仅作记录。')
  }

  const route = buildRoutePlan({
    player,
    quests,
    outposts,
    resources,
    calibrations
  })

  return {
    capture: parseCapture(raw, integrityStatus),
    player,
    statistics: parseStatistics(save),
    quests,
    outposts,
    resources,
    maps,
    calibrations,
    route,
    warnings
  }
}
